import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { MapPin, Search, Loader2, Plus, Minus } from 'lucide-react';

const LIBRARIES = ['places'];

const DEFAULT_CENTER = {
    lat: 21.1702,
    lng: 72.8311
};

const MapControlStyles = () => {
    useEffect(() => {
        const styleId = 'map-control-styles';
        if (document.getElementById(styleId)) return;
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            @media (max-width: 767px) {
                /* ── Map / Satellite toggle ── */
                .gm-style-mtc {
                    display: flex !important;
                    flex-direction: row !important;
                }
                .gm-style-mtc button {
                    font-size: 10px !important;
                    padding: 2px 8px !important;
                    height: 22px !important;
                    line-height: 1 !important;
                    min-width: unset !important;
                }
                .gm-style-mtc ul,
                .gm-style-mtc li {
                    display: none !important;
                }
            }
        `;
        document.head.appendChild(style);
        return () => {
            const el = document.getElementById(styleId);
            if (el) el.remove();
        };
    }, []);
    return null;
};

const LocationPicker = ({ value, onChange, apiKey, error, touched }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: apiKey || "",
        libraries: LIBRARIES
    });

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth < 768;

    const options = React.useMemo(() => {
        const isMobile = windowWidth < 768;
        return {
            disableDefaultUI: true,
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
                position: isMobile
                    ? window.google?.maps?.ControlPosition?.BOTTOM_CENTER
                    : window.google?.maps?.ControlPosition?.BOTTOM_LEFT,
            },
            // Disable built-in zoom — we use custom buttons instead
            zoomControl: false,
            streetViewControl: false,
            fullscreenControl: !isMobile,
            fullscreenControlOptions: {
                position: window.google?.maps?.ControlPosition?.RIGHT_TOP,
            },
            scaleControl: false,
            rotateControl: false,
            clickableIcons: false,
        };
    }, [windowWidth]);

    const [map, setMap] = useState(null);
    const [searchBox, setSearchBox] = useState(null);
    const [markerPosition, setMarkerPosition] = useState(
        value ? { lat: value.lat, lng: value.lng } : null
    );

    useEffect(() => {
        if (value?.lat && value?.lng) {
            setMarkerPosition({ lat: value.lat, lng: value.lng });
        }
    }, [value]);

    const onLoad = useCallback((map) => setMap(map), []);
    const onUnmount = useCallback(() => setMap(null), []);

    const onMapClick = useCallback((e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMarkerPosition({ lat, lng });
        onChange({ lat, lng });
    }, [onChange]);

    const onSearchLoad = (ref) => setSearchBox(ref);

    const onPlacesChanged = () => {
        const places = searchBox.getPlaces();
        if (!places?.length) return;
        const place = places[0];
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMarkerPosition({ lat, lng });
        map.panTo({ lat, lng });
        map.setZoom(15);
        onChange({ lat, lng, address: place.formatted_address });
    };

    const handleZoomIn = () => {
        if (map) map.setZoom(map.getZoom() + 1);
    };

    const handleZoomOut = () => {
        if (map) map.setZoom(map.getZoom() - 1);
    };

    if (loadError) {
        return (
            <div className="p-4 bg-red-50 text-red-500 rounded-xl border border-red-100 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>Error loading Google Maps. Please check your API Key.</span>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="h-[400px] w-full bg-slate-50 rounded-xl border border-gray-100 flex items-center justify-center text-slate-400 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading Map...</span>
            </div>
        );
    }

    const mapContainerStyle = {
        width: '100%',
        height: '100%',
        borderRadius: '0.75rem',
        border: (touched && error) ? '1px solid #ef4444' : '1px solid transparent'
    };

    return (
        <div className="space-y-4 h-full">
            <MapControlStyles />

            <div className="relative h-full">

                {/* Search box — top left */}
                <div className="absolute top-3 left-3 z-10 w-[62%] max-w-xs md:w-full md:max-w-sm">
                    <StandaloneSearchBox onLoad={onSearchLoad} onPlacesChanged={onPlacesChanged}>
                        <div className="relative shadow-lg">
                            <input
                                type="text"
                                placeholder="Search for a location..."
                                className="w-full px-4 py-2.5 pl-10 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-secondary/50 text-sm font-medium"
                            />
                            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        </div>
                    </StandaloneSearchBox>
                </div>

                {/* Custom Zoom buttons — right side, vertically centered */}
                <div
                    className="absolute z-10 flex flex-col shadow-md overflow-hidden"
                    style={{
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        borderRadius: 6,
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <button
                        onClick={handleZoomIn}
                        className="bg-white hover:bg-slate-50 active:bg-slate-100 flex items-center justify-center transition-colors"
                        style={{ width: 30, height: 30, borderBottom: '1px solid #e2e8f0' }}
                        aria-label="Zoom in"
                    >
                        <Plus className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        className="bg-white hover:bg-slate-50 active:bg-slate-100 flex items-center justify-center transition-colors"
                        style={{ width: 30, height: 30 }}
                        aria-label="Zoom out"
                    >
                        <Minus className="w-4 h-4 text-slate-600" />
                    </button>
                </div>

                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={markerPosition || DEFAULT_CENTER}
                    zoom={markerPosition ? 15 : 12}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onClick={onMapClick}
                    options={options}
                >
                    {markerPosition && (
                        <Marker
                            position={markerPosition}
                            animation={window.google.maps.Animation.DROP}
                        />
                    )}
                </GoogleMap>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2 text-xs text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                <span>Search or click on the map to pin the exact location</span>
                {markerPosition && (
                    <span className="ml-auto font-mono bg-slate-100 px-2 py-0.5 rounded">
                        {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
                    </span>
                )}
            </div>
        </div>
    );
};

export default LocationPicker;